import { useRef, useState, useEffect } from 'react';
import {
  PencilSquareIcon,
  BoltIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  EyeIcon,
  FaceSmileIcon,
} from '@heroicons/react/24/outline';
import { commentReplyById, getCommentById } from '../../api/comment.ts';
import { formatDate } from '../../utils/formatDate.tsx';
import { Post, Comment } from '../Post/post.ts';
import {
  ChatBubbleOvalLeftIcon,
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/solid';
import { Send, Magic } from 'react-bootstrap-icons';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';

const CommentReply = ({
  isLoading,
  comments,
  setComments,
  selectedComment,
  setSelectedComment,
  currentSocial,
  replyToComment,
  isCommentHidden = false,
  updateCommentHiddenStatus,
}: {
  isLoading: boolean;
  comments: any;
  setComments: any;
  selectedComment: number | null | undefined;
  setSelectedComment: any;
  currentSocial: any;
  replyToComment: (platformCommentId: string, message: string) => Promise<void>;
  isCommentHidden?: boolean;
  updateCommentHiddenStatus?: () => void;
}) => {
  const [isSending, setIsSending] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [message, setMessage] = useState('');
  const [draftComment, setDraftComment] = useState('');
  const [sendingComments, setSendingComments] = useState<string[]>([]);
  const queue = useRef<any[]>([]);
  const isProcessing = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(true);

  const handleEmojiSelect = (emoji: { native: string | any[] }) => {
    // Insert emoji at current cursor position or at the end
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newMessage =
        message.substring(0, start) + emoji.native + message.substring(end);
      setMessage(newMessage);

      // Set cursor position after the inserted emoji
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = start + emoji.native.length;
        textarea.selectionEnd = start + emoji.native.length;
      }, 0);
    } else {
      setMessage(message + emoji.native);
    }

    setShowEmojiPicker(false);
  };

  useEffect(() => {
    interface ClickOutsideEvent extends MouseEvent {}

    const handleClickOutside = (event: ClickOutsideEvent): void => {
      // Check if click target is the emoji button
      const target = event.target as Element;
      const isEmojiButton =
        target.closest('button[title="Close emoji picker"]') ||
        target.closest('button[title="Add emoji"]');

      // Only close if:
      // 1. Emoji picker is open AND
      // 2. Click is outside the picker container AND
      // 3. Click is not on the emoji button itself
      if (
        showEmojiPicker &&
        !target.closest('.emoji-picker-container') &&
        !isEmojiButton
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
    if (
      selectedComment !== null &&
      selectedComment !== undefined &&
      comments[selectedComment]
    ) {
      const selected = comments[selectedComment];
      setMessage(selected?.LLMResponse || '');
      setDraftComment(selected?.LLMResponse || '');
    } else {
      setMessage('');
      setDraftComment('');
    }
  }, [selectedComment, comments]);

  const focusOnTextarea = () => {
    textareaRef.current?.focus();
    textareaRef.current?.setSelectionRange(
      textareaRef.current.value.length,
      textareaRef.current.value.length
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  const processQueue = async () => {
    if (isProcessing.current) return;

    isProcessing.current = true;

    while (queue.current.length > 0) {
      const { platformCommentId, message } = queue.current.shift();

      setSendingComments((prev) => [...prev, platformCommentId]);
      setIsSending(true);
      await replyToComment(platformCommentId, message);
      // await refreshPost()
      setSendingComments((prev) =>
        prev.filter((id) => id !== platformCommentId)
      );
    }

    setIsSending(false);
    isProcessing.current = false;
  };

  const queueRequest = (platformCommentId: string, message: string) => {
    queue.current.push({ platformCommentId, message });
    processQueue();
  };

  const publishComment = () => {
    if (selectedComment === null || selectedComment === undefined) return;

    const updatedComments = [...comments];
    const index = selectedComment;
    const platformCommentId = updatedComments[index].platformCommentId;

    const updatedComment = (updatedComments[index] = {
      ...updatedComments[index],
      status: 2,
      pageOwnerReply: {
        message: message,
        platformCreatedAt: new Date().toISOString(),
      },
    });
    updatedComments[index] = updatedComment;
    setComments(updatedComments);
    queueRequest(platformCommentId, message);

    setTimeout(() => {
      if (index + 1 < updatedComments.length) {
        setSelectedComment(index + 1);
      } else if (updatedComments.length > 1) {
        setSelectedComment(0);
      } else {
        setSelectedComment(null);
      }
    }, 500);
  };

  const hasReply =
    selectedComment !== null &&
    selectedComment !== undefined &&
    comments[selectedComment]?.status === 2;

  const firstReplyFromUser =
    selectedComment !== null &&
    selectedComment !== undefined &&
    comments[selectedComment]?.pageOwnerReply;

  const defaultReply = {
    platformCreatedAt: 'No timestamp loaded',
    message: 'No reply loaded',
  };
  const safeReply = firstReplyFromUser ?? defaultReply;

  const attachment =
    selectedComment !== null
      ? // @ts-ignore
        comments[selectedComment]?.reply?.data?.[0]?.attachment_url
      : null;

  const regenerateComment = async () => {
    if (selectedComment === null || selectedComment === undefined) return;

    try {
      setIsRegenerating(true);
      const platformCommentId = comments[selectedComment].platformCommentId;

      const newComment = await getCommentById(
        platformCommentId,
        currentSocial.id,
        true
      );

      const updatedComments = comments.map((c: any) =>
        c.platformCommentId === platformCommentId
          ? { ...c, ...newComment.data }
          : c
      );
      setComments(updatedComments);

      setSelectedComment(
        updatedComments.findIndex(
          (c: any) => c.platformCommentId === platformCommentId
        )
      );
      setMessage(newComment.data.LLMResponse);
    } catch (error: any) {
      console.log(error);
    } finally {
      setIsRegenerating(false);
    }
  };

  const goToNextComment = () => {
    if (selectedComment === null || selectedComment === undefined) return;

    const index = selectedComment;
    if (index + 1 < comments.length) {
      setSelectedComment(index + 1);
    } else if (comments.length > 1) {
      setSelectedComment(0);
    } else {
      setSelectedComment(null);
    }
  };

  const goToPreviousComment = () => {
    if (selectedComment === null || selectedComment === undefined) return;

    const index = selectedComment;
    if (index > 0) {
      setSelectedComment(index - 1);
    } else if (comments.length > 1) {
      setSelectedComment(comments.length - 1);
    } else {
      setSelectedComment(null);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Special handling for textarea
      if (e.target instanceof HTMLTextAreaElement) {
        // Handle Escape key to blur/exit textarea focus
        if (e.key === 'Escape') {
          e.preventDefault();
          textareaRef.current?.blur();
          return;
        }

        // Only process Enter key for publishing when in textarea
        if (
          e.key === 'Enter' &&
          !e.shiftKey &&
          !hasReply &&
          !isLoading &&
          !isCommentHidden
        ) {
          e.preventDefault(); // Prevent default (newline)
          publishComment();
          return;
        }
        // Let Shift+Enter create a newline (default behavior)
        return; // Exit, don't process other shortcuts while in textarea
      }

      // For non-textarea elements, process all shortcuts
      if (e.target instanceof HTMLInputElement) {
        return; // Skip shortcuts for other input fields
      }

      // Regular keyboard shortcuts
      switch (e.key) {
        case 'w':
          e.preventDefault();
          goToPreviousComment();
          break;
        case 's':
          e.preventDefault();
          goToNextComment();
          break;
        case 'Enter':
          if (!hasReply && !isLoading && !isCommentHidden) {
            e.preventDefault();
            publishComment();
          }
          break;
        case 'r':
        case 'R':
          if (!isLoading && !isCommentHidden) {
            e.preventDefault();
            regenerateComment();
          }
          break;
        case 'h':
        case 'H':
          if (updateCommentHiddenStatus) {
            e.preventDefault();
            updateCommentHiddenStatus();
          }
          break;
        case 'e':
          if (!hasReply && !isLoading && !isCommentHidden) {
            e.preventDefault();
            focusOnTextarea();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    selectedComment,
    hasReply,
    isLoading,
    isCommentHidden,
    message,
    publishComment,
  ]);

  return (
    <div className="mt-2">
      <div className="mx-3 flex flex-col justify-between bg-white bg-opacity-5 rounded-2xl p- mt-4 min-h-44">
        {isLoading ? (
          <div className="animate-pulse ml-2 mt-2 mb-auto h-6 p-2 pb-4 bg-white bg-opacity-10 w-3/4 rounded-md text-sm" />
        ) : hasReply ? (
          <div className="w-full text-white bg-white bg-opacity-0 rounded-lg p-3 text-sm">
            {attachment && (
              <img className="w-auto h-20" src={attachment} alt="Attachment" />
            )}
            {safeReply.message}
          </div>
        ) : (
          <div className="w-full p-1 relative">
            <textarea
              rows={6}
              className="w-full text-white bg-white bg-opacity-0 rounded-lg resize-none p-2 text-sm"
              value={message}
              onChange={handleChange}
              ref={textareaRef}
              disabled={hasReply}
              placeholder="Write your comment here..."
            />
            <div className="flex justify-between mt-2 pl-2">
              <div className="flex flex-row items-center relative">
                <button
                  className={`${
                    showEmojiPicker
                      ? 'text-yellow'
                      : 'text-white hover:text-yellow'
                  } mr-2`}
                  onClick={() => {
                    setShowEmojiPicker((current) => !current);
                  }}
                  disabled={hasReply} // Removed showEmojiPicker from here
                  title={showEmojiPicker ? 'Close emoji picker' : 'Add emoji'}
                >
                  <FaceSmileIcon className={`w-4 h-4`} />
                </button>
                {showEmojiPicker && (
                  <div className="absolute bottom-8 left-0 z-50 emoji-picker-container">
                    <Picker
                      data={data}
                      onEmojiSelect={handleEmojiSelect}
                      theme="dark"
                      searchPosition="top"
                      previewPosition="none"
                      skinTonePosition="none"
                      perLine={8}
                      maxFrequentRows={2}
                    />
                  </div>
                )}
                <div className="text-xs text-white text-opacity-70">
                  Shift + Enter to add a new line
                </div>
              </div>
              <button
                className="flex flex-row items-center text-yellow hover:brightness-90 pr-3"
                onClick={focusOnTextarea}
              >
                Edit
                <PencilSquareIcon className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        )}

        {hasReply &&
        !sendingComments.includes(
          comments[selectedComment as number].platformCommentId
        ) &&
        !isRegenerating ? (
          <div className="flex flex-row justify-between items-end p-2">
            <div className="flex flex-row items-center text-xs text-white text-opacity-70">
              <CheckCircleIcon className="w-4 h-4 text-green-400 mr-1" />
              Reply posted: {formatDate(safeReply.platformCreatedAt)}
            </div>
          </div>
        ) : sendingComments.includes(
            comments[selectedComment as number].platformCommentId
          ) ? (
          <div className="flex flex-row justify-between items-end p-2">
            <div className="flex flex-row items-center text-xs text-white text-opacity-70">
              <ArrowPathIcon className={`w-4 h-4 mr-1 animate-spin`} />
              Posting reply...
            </div>
          </div>
        ) : null}
      </div>

      {!hasReply && !isLoading && (
        <div className="mx-3 flex justify-between mt-3 relative">
          <button
            onClick={regenerateComment}
            disabled={isRegenerating}
            className={`
              flex flex-row items-center
              text-white hover:brightness-90
              px-3 py-1.5 bg-white bg-opacity-10 rounded-3xl
              ${
                isRegenerating
                  ? 'shadow-[0_0_15px_rgba(147,51,234,0.7)] animate-[pulseViolet_1.5s_infinite]'
                  : ''
              }
              transition-all duration-200
              hover:shadow-[0_0_15px_rgba(147,51,234,0.5)] 
              ${
                !isRegenerating
                  ? 'hover:animate-[pulseViolet_1.5s_infinite]'
                  : ''
              }
            `}
            style={
              isRegenerating
                ? {
                    boxShadow:
                      '0 0 5px rgba(147,51,234,0.5), 0 0 15px rgba(147,51,234,0.5), 0 0 25px rgba(147,51,234,0.3)',
                  }
                : ({
                    '--hover-shadow':
                      '0 0 5px rgba(147,51,234,0.5), 0 0 15px rgba(147,51,234,0.5), 0 0 25px rgba(147,51,234,0.3)',
                  } as React.CSSProperties)
            }
          >
            {isRegenerating ? (
              <>
                <ArrowPathIcon className="w-4 h-4 mr-1 animate-spin" />
                <span>Regenerating...</span>
              </>
            ) : (
              <>
                <span>Regenerate</span>
                <Magic className="w-4 h-4 ml-1 fill-white" />
              </>
            )}
          </button>

          <style>{`
            button:hover:not(:disabled) {
              box-shadow: var(--hover-shadow);
            }
            
            @keyframes pulseViolet {
              0%, 100% {
                box-shadow: 0 0 5px rgba(147,51,234,0.5), 0 0 15px rgba(147,51,234,0.5);
              }
              50% {
                box-shadow: 0 0 10px rgba(147,51,234,0.7), 0 0 20px rgba(147,51,234,0.5), 0 0 30px rgba(147,51,234,0.3);
              }
            }
          `}</style>

          <div className="relative">
            <button
              className={`
                flex items-center bg-yellow text-black px-4 py-1.5 rounded-3xl 
                whitespace-nowrap hover:brightness-95
                transition-all duration-200
                hover:animate-[pulse_1.5s_infinite]
                hover:shadow-[0_0_15px_rgba(234,179,8,0.7)]
              `}
              style={
                {
                  '--hover-shadow':
                    '0 0 5px rgba(234,179,8,0.5), 0 0 15px rgba(234,179,8,0.5), 0 0 25px rgba(234,179,8,0.3)',
                } as React.CSSProperties
              }
              onClick={() => {
                if (isCommentHidden) {
                  setShowConfirmation(true);
                } else {
                  publishComment();
                }
              }}
              disabled={isRegenerating}
            >
              <span className="mr-2">Publish reply</span>
              <Send className="w-4 h-4 flex-shrink-0" />
            </button>

            <style>{`
              button:hover {
                box-shadow: var(--hover-shadow);
              }
              
              @keyframes pulse {
                0%, 100% {
                  box-shadow: 0 0 5px rgba(234,179,8,0.5), 0 0 15px rgba(234,179,8,0.5);
                }
                50% {
                  box-shadow: 0 0 10px rgba(234,179,8,0.7), 0 0 20px rgba(234,179,8,0.5), 0 0 30px rgba(234,179,8,0.3);
                }
              }
            `}</style>

            {/* Confirmation Popup */}
            {showConfirmation && (
              <div className="absolute right-0 bottom-12 w-64 bg-gray-800 border border-gray-700 rounded-md shadow-lg z-50 text-white text-sm">
                <div className="p-3">
                  <p className="mb-3">
                    This comment is <span className="text-red">hidden.</span>{' '}
                    <br />
                    Replying will keep it hidden.
                  </p>
                  <div className="flex justify-end space-x-2">
                    <button
                      className="px-3 py-1 bg-gray-700 rounded-md hover:bg-gray-600"
                      onClick={() => setShowConfirmation(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-3 py-1 bg-yellow text-black rounded-md hover:brightness-90"
                      onClick={() => {
                        publishComment();
                        setShowConfirmation(false);
                      }}
                    >
                      Reply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Add keyboard shortcuts help */}
      <div className="text-xs text-gray-400 mt-2 flex justify-start relative mx-3">
        <div
          className="group cursor-pointer flex items-center"
          onClick={() => setShowShortcuts(false)}
          onMouseEnter={() => {
            if (!showShortcuts) setShowShortcuts(true);
          }}
          onMouseLeave={() => {
            if (!showShortcuts) setShowShortcuts(false);
          }}
        >
          <InformationCircleIcon className="w-5 h-5 mr-2" />
          <span>Keyboard shortcuts</span>
          {showShortcuts && (
            <div
              className="absolute bg-grey text-white text-left p-2 rounded-md z-10 top-full mb-2 left-1/2 -translate-x-1/2 shadow-lg overflow-y-auto 
            w-[360px] sm:w-[400px] md:w-[420px] 
            max-h-[280px] sm:max-h-[300px] md:max-h-[380px]"
            >
              <h4 className="font-semibold border-b border-gray-600 pb-1 mb-1 text-xs">
                Keyboard Shortcuts
              </h4>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                <span>
                  <kbd className="bg-gray-700 px-1 py-0.5 rounded">w</kbd>
                  <kbd className="bg-gray-700 px-1 py-0.5 rounded ml-1">
                    s
                  </kbd>{' '}
                  Navigate comments
                </span>
                <span>
                  <kbd className="bg-gray-700 px-1 py-0.5 rounded">a</kbd>
                  <kbd className="bg-gray-700 px-1 py-0.5 rounded ml-1">
                    d
                  </kbd>{' '}
                  Change page
                </span>
                <span>
                  <kbd className="bg-gray-700 px-1 py-0.5 rounded">R</kbd>{' '}
                  Regenerate
                </span>
                <span>
                  <kbd className="bg-gray-700 px-1 py-0.5 rounded">H</kbd>{' '}
                  Hide/Unhide
                </span>
                <span>
                  <kbd className="bg-gray-700 px-1 py-0.5 rounded">Space</kbd>{' '}
                  Edit
                </span>
                <span>
                  <kbd className="bg-gray-700 px-1 py-0.5 rounded">Enter</kbd>{' '}
                  Publish reply
                </span>
              </div>
              <div className="text-right mt-2">
                <button
                  className="text-xs text-gray-300 hover:text-white underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowShortcuts(false);
                  }}
                >
                  Hide
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="my-4"></div>
    </div>
  );
};

export default CommentReply;
